import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Image, Text, useToast } from "@chakra-ui/react";

// Ужимает картинку в квадрат 256×256 и возвращает data-URL (JPEG)
const resizeImage = (file, size = 256) =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      canvas
        .getContext("2d")
        .drawImage(img, sx, sy, side, side, 0, 0, size, size);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const CameraGlyph = ({ color = "#243D27" }) => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="2.5"
      y="6.5"
      width="19"
      height="14"
      rx="3.5"
      stroke={color}
      strokeWidth="1.6"
    />
    <path
      d="M8 6.5l1.2-2a1.5 1.5 0 011.3-.75h3a1.5 1.5 0 011.3.75l1.2 2"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="12" cy="13.5" r="4" stroke="#8D414B" strokeWidth="1.6" />
    <circle cx="18.2" cy="10" r="0.9" fill="#8D414B" />
  </svg>
);

const AvatarUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const toast = useToast();

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        toast({
          title: "Нужно изображение (JPG, PNG…)",
          status: "warning",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
        return;
      }
      try {
        const dataUrl = await resizeImage(file);
        onChange(dataUrl);
      } catch (e) {
        toast({
          title: "Не удалось прочитать изображение",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "bottom",
        });
      }
    },
    [onChange, toast]
  );

  // Вставка из буфера обмена (Cmd/Ctrl+V)
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e.preventDefault();
          handleFile(item.getAsFile());
          return;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handleFile]);

  if (value) {
    return (
      <Box
        d="flex"
        alignItems="center"
        bg="#F6E8C6"
        border="1px solid #E4D2A6"
        borderRadius="14px"
        p={3}
      >
        <Image
          src={value}
          alt="Аватар"
          boxSize="56px"
          borderRadius="full"
          objectFit="cover"
          border="2px solid #8D414B"
          boxShadow="0 6px 16px -8px rgba(141, 65, 75, 0.6)"
        />
        <Box ml={3} flex="1">
          <Text fontSize="sm" fontWeight={500} color="#243D27">
            Аватар выбран
          </Text>
          <Box d="flex" mt={1}>
            <Button
              size="xs"
              variant="outline"
              colorScheme="pine"
              borderRadius="8px"
              mr={2}
              onClick={() => inputRef.current?.click()}
            >
              Заменить
            </Button>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="burgundy"
              borderRadius="8px"
              onClick={() => onChange("")}
            >
              Убрать
            </Button>
          </Box>
        </Box>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            handleFile(e.target.files[0]);
            e.target.value = "";
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        handleFile(e.dataTransfer.files[0]);
      }}
      cursor="pointer"
      d="flex"
      flexDir="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      py={5}
      px={4}
      bg={dragOver ? "#FFFBF0" : "#FDF7E9"}
      border="1.5px dashed"
      borderColor={dragOver ? "#8D414B" : "#D3BC8D"}
      borderRadius="14px"
      transition="all 0.18s ease"
      transform={dragOver ? "scale(1.01)" : "none"}
      boxShadow={
        dragOver ? "0 10px 26px -14px rgba(141, 65, 75, 0.55)" : "none"
      }
      _hover={{ borderColor: "#8D414B", bg: "#FFFBF0" }}
      _focus={{ outline: "none", borderColor: "#8D414B" }}
    >
      <CameraGlyph />
      <Text fontSize="sm" fontWeight={500} color="#243D27" mt={2}>
        {dragOver ? "Отпускайте!" : "Перетащите изображение сюда"}
      </Text>
      <Text fontSize="xs" color="rgba(36, 61, 39, 0.6)" mt={1}>
        …или нажмите, чтобы выбрать файл, или вставьте из буфера — ⌘V
      </Text>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          handleFile(e.target.files[0]);
          e.target.value = "";
        }}
      />
    </Box>
  );
};

export default AvatarUpload;
